import type { GameId } from './games/types';

/**
 * The casino's floors. Every floor keeps the games from the floors below and
 * adds its own; every bet limit is the game's base limit times the floor's
 * multiplier, so the same game costs more the higher you climb (Main Floor
 * slots start at $5, Mezzanine slots at $50).
 */
export interface FloorDef {
  id: number;
  name: string;
  /** Price to ride the elevator up TO this floor. */
  elevatorCost: number;
  betMultiplier: number;
  /** Games that first appear on this floor. */
  newGames: GameId[];
}

export const FLOORS: FloorDef[] = [
  { id: 1, name: 'Main Floor', elevatorCost: 0, betMultiplier: 1, newGames: ['slots', 'blackjack', 'scratchers'] },
  // Floor re-mix (2026-09-23, user): every floor has a skill game, deeper as
  // you climb — Blackjack, Liar's Dice, Hold'em, Video Poker, Mahjong.
  // The first ride was $300 (user, 2026-09-23: cut to make floor 1 quicker).
  { id: 2, name: 'Mezzanine', elevatorCost: 150, betMultiplier: 10, newGames: ['crash', 'baccarat', 'highlow', 'liarsdice'] },
  { id: 3, name: 'Salon Prive', elevatorCost: 1500, betMultiplier: 25, newGames: ['mines', 'roulette', 'craps', 'threecard', 'holdem'] },
  // Keno and Sic Bo were cut (2026-09-23, user: no real strategy / not as
  // fun); Casino War and Dice moved down from the Penthouse to fill in.
  { id: 4, name: 'High Limit Room', elevatorCost: 7500, betMultiplier: 100, newGames: ['bigsix', 'war', 'dice', 'videopoker'] },
  { id: 5, name: 'Penthouse', elevatorCost: 30000, betMultiplier: 250, newGames: ['tower', 'plinko', 'horses', 'mahjong'] },
];

/** Highest floor the elevator can reach. The Penthouse is the top: Buy the
 *  Casino (Gift Shop) is how you win from there. */
export const TOP_BUILT_FLOOR = 5;

/** Progressive jackpot starting pot, in base dollars (x the floor multiplier). */
export const JACKPOT_SEED = 50;
/** Share of every settled bet the house adds to the floor's pot. It's paid
 *  by the house, not skimmed from the player, so it's pure upside. */
export const JACKPOT_CONTRIBUTION = 0.02;

export function floorDef(id: number): FloorDef {
  return FLOORS.find((f) => f.id === id) ?? FLOORS[0]!;
}

export function nextFloor(id: number): FloorDef | null {
  if (id >= TOP_BUILT_FLOOR) return null;
  return FLOORS.find((f) => f.id === id + 1) ?? null;
}

/** Every game playable on a floor: its own plus all the floors below. */
export function gamesOnFloor(id: number): GameId[] {
  return FLOORS.filter((f) => f.id <= id).flatMap((f) => f.newGames);
}

export function jackpotSeed(id: number): number {
  return JACKPOT_SEED * floorDef(id).betMultiplier;
}
